import { Article, FlightStatus } from 'models/shipments';
import { AppThunkAction } from 'models/Store';
import { formatTimeWindow, getEddRangeText, getEddText, isToday } from 'utils/date.utils';

import { setDeliveryEstimateAnalytics } from '../analytics';
import { setDeliveryEstimateInfo } from './deliveryEstimate.slice';

export interface DeliveryEstimateInfo {
  eddText: string | null;
  timeWindowText: string | null;
  latestEdd: string | null;
  flightStatus: FlightStatus;
  analytics: string | null;
}

export const updateDeliveryEstimateInfo =
  (currentArticle: Article): AppThunkAction =>
  (dispatch) => {
    const deliveryEstimateInfo = transformDeliveryEstimateInfo(currentArticle);
    dispatch(setDeliveryEstimateInfo(deliveryEstimateInfo));
    dispatch(setDeliveryEstimateAnalytics(deliveryEstimateInfo));
  };

export function transformDeliveryEstimateInfo(article: Article): DeliveryEstimateInfo {
  const { timeWindow, estimatedDeliveryDateRange, deliveredByDateISO, dateRevised } = article;

  const deliveryEstimateInfo: DeliveryEstimateInfo = {
    eddText: null,
    timeWindowText: null,
    latestEdd: null,
    flightStatus: dateRevised ? FlightStatus.UPDATED : FlightStatus.ON_TIME,
    analytics: 'edd not shown',
  };

  if (timeWindow) {
    const { start, end, passedWindow, deliveryDate } = timeWindow;
    if (passedWindow && deliveryDate && !isToday(deliveryDate)) {
      deliveryEstimateInfo.eddText = getEddText(deliveryDate);
      deliveryEstimateInfo.flightStatus = FlightStatus.LATE;
      deliveryEstimateInfo.latestEdd = deliveryDate;
      deliveryEstimateInfo.analytics = 'edd shown after time window';
      return deliveryEstimateInfo;
    }

    if (start && end) {
      const windowStart = formatTimeWindow(start);
      const windowEnd = formatTimeWindow(end);
      deliveryEstimateInfo.timeWindowText = `${windowStart}–${windowEnd}`;
      deliveryEstimateInfo.analytics = 'time window shown';
      return deliveryEstimateInfo;
    }
  }

  if (estimatedDeliveryDateRange) {
    const { fromDate, toDate } = estimatedDeliveryDateRange;
    deliveryEstimateInfo.eddText = getEddRangeText(fromDate, toDate);
    deliveryEstimateInfo.latestEdd = toDate;
    if (deliveryEstimateInfo.eddText) {
      deliveryEstimateInfo.analytics = 'edd range shown';
      return deliveryEstimateInfo;
    }
  }

  if (deliveredByDateISO) {
    deliveryEstimateInfo.latestEdd = deliveredByDateISO;
    deliveryEstimateInfo.eddText = getEddText(deliveredByDateISO);
    deliveryEstimateInfo.analytics = 'edd shown';
  }

  return deliveryEstimateInfo;
}
